import type { jsPDF } from 'jspdf';
import { BcraResult, BcraRiskEstimate, estimateSummary, GAIL_LIMITATIONS, reportAbout, RISK_COPY, SCREENING_FACTORS } from './bcra-report.model';

// Figma: IVY-HA, nodes 50:16287 (page 1) and 50:17209 (page 2). US Letter, units in pt.
const PAGE_W = 612;
const CONTENT_X = 42;
const CONTENT_W = 529;
const CONTENT_TOP = 72;
const CONTENT_BOTTOM = 752;
const GAP = 10;
const TEXT = 12;
const LINE = 15;

const COLOR = {
  black: '#000000',
  headerBg: '#F7F7FB',
  logoBg: '#32286C',
  divider: '#F1F1F2',
  footerText: '#4C4F53',
  tint: '#F4F2FF',
  chartBorder: '#DAD6FB',
  averageBar: '#CABFFF',
  riskBar: '#5D38F1',
};

interface Run {
  text: string;
  bold?: boolean;
}

interface Word {
  text: string;
  bold: boolean;
  width: number;
}

export interface BcraPdfOptions {
  userName: string;
  generatedAt: Date;
}

export async function downloadBcraReportPdf(result: BcraResult, options: BcraPdfOptions): Promise<void> {
  const [{ jsPDF }, logo] = await Promise.all([import('jspdf'), rasterizeSvg('assets/report/pdf-logo.svg', 31, 8)]);
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pdf = new PdfWriter(doc, () => drawHeader(doc, logo, options));

  drawHeader(doc, logo, options);
  drawFirstPage(pdf, result);
  pdf.newPage();
  drawSecondPage(pdf, result);
  drawFooters(doc);

  doc.save('IVY-Breast-Cancer-Risk-Assessment-Report.pdf');
}

function drawFirstPage(pdf: PdfWriter, result: BcraResult) {
  const answeredOn = result.answeredAt ? formatDayMonthYear(result.answeredAt) : 'DD/MM/YYYY';
  const above = result.estimates[0].risk > result.estimates[0].averageRisk;

  pdf.paragraph([{ text: 'Breast Cancer Risk Assessment Report', bold: true }], { size: 19, line: 20 });
  pdf.gap(GAP);
  pdf.paragraph([{
    text: `This report was created from the answers you provided in the Breast Cancer Risk Assessment questionnaire at ivyhealth.ai on ${answeredOn}. To update your answers, sign in to your IVY account.`,
    bold: true,
  }]);
  pdf.gap(GAP);

  // 5-year chart first, then lifetime when available
  for (const estimate of result.estimates) {
    pdf.gap(10);
    drawChart(pdf, estimate);
    pdf.paragraph([{ text: toTitleCase(RISK_COPY[estimate.kind].caption), bold: true }], { line: 28, align: 'center' });
    pdf.gap(10 + GAP);

    pdf.box([{ text: estimateSummary(estimate), bold: true }], { padX: 10, padY: 10, align: 'center' });
    pdf.gap(GAP);
  }

  pdf.heading('What this result means');
  pdf.paragraph(above
    ? [{ text: 'Your estimate is above the model’s average.', bold: true }, { text: ' It does not mean you have breast cancer or will develop it.' }]
    : [{ text: 'Your estimate is at or below the model’s average.', bold: true }, { text: ' It does not mean you cannot develop breast cancer.' }]);
  pdf.paragraph([{ text: 'Use this result to start a conversation about your breast health. Your healthcare professional can help put it in context.' }]);
  pdf.gap(GAP);

  pdf.heading('What to do next');
  pdf.paragraph([{ text: 'Discuss your result with a healthcare professional', bold: true }]);
  pdf.paragraph([{ text: 'Share this report with your healthcare professional. They can review your result alongside your age, breast density, personal and family history, and help determine which screening schedule is appropriate for you.' }]);
}

function drawSecondPage(pdf: PdfWriter, result: BcraResult) {
  pdf.pill(`Your Breast Density: ${result.breastDensity}`);
  pdf.gap(GAP);

  pdf.box([
    { text: 'Breast density is not included in your Gail estimate.', bold: true },
    { text: ' Dense tissue can increase breast cancer risk and make cancer harder to see on a mammogram. Ask your healthcare professional whether additional screening would be helpful for you.' },
  ], { padX: 30, padY: 15 });
  pdf.gap(GAP);

  pdf.heading('Factors that may affect your screening');
  pdf.paragraph([{ text: 'Your risk estimate is only one part of the picture', bold: true }]);
  pdf.paragraph([{ text: 'The type and frequency of screening that may be appropriate for you can also depend on:' }]);
  for (const factor of SCREENING_FACTORS) {
    pdf.paragraph([{ text: `• ${factor}` }]);
  }
  pdf.gap(GAP);

  pdf.heading('About this estimate', 20);
  pdf.paragraph([{ text: reportAbout(result) }]);
  pdf.gap(LINE);
  pdf.paragraph([{ text: 'Important limitations', bold: true }]);
  pdf.gap(LINE);
  pdf.paragraph([{ text: 'The Gail model does not include breast density or extended family history and is intended for women ages 35–85. It may not provide an accurate risk estimate if you:' }]);
  for (const limitation of GAIL_LIMITATIONS) {
    pdf.paragraph([{ text: `• ${limitation}` }]);
  }
  pdf.paragraph([{ text: 'The model uses U.S. data, and its accuracy may vary across populations. This result is a statistical estimate—not a diagnosis or personalized screening recommendation. Discuss your result and screening options with a healthcare professional.' }]);
}

// Breast Cancer Risk Chart - Figma node 50:17154
function drawChart(pdf: PdfWriter, estimate: BcraRiskEstimate) {
  const doc = pdf.doc;
  const boxH = 144;
  pdf.ensure(boxH);
  const top = pdf.y;

  doc.setDrawColor(COLOR.chartBorder);
  doc.setLineWidth(2);
  doc.roundedRect(CONTENT_X + 1, top + 1, CONTENT_W - 2, boxH - 2, 10, 10, 'S');

  // Chart origin, measured from the Figma chart group
  const x0 = CONTENT_X + 14;
  const y0 = top + 12;
  const plotX = x0 + 78.86;
  const plotW = 5 * 82.39;
  const plotH = 100.44;
  const { max, ticks, highlightedTick } = estimate.scale;
  const toX = (percent: number) => plotX + Math.min(percent, max) / max * plotW;

  doc.setFillColor(COLOR.black);
  doc.setDrawColor(COLOR.black);
  for (const tick of ticks) {
    const x = toX(tick);
    const highlighted = tick === highlightedTick;
    doc.setLineWidth(highlighted ? 2 : 0.75);
    doc.line(x, y0, x, y0 + plotH);
    doc.circle(x, y0 + plotH, highlighted ? 3 : 1.75, 'F');
    pdf.text(`${tick}%`, x, y0 + plotH + 10, { size: 10, bold: true, align: 'center', baseline: 'middle' });
  }
  doc.setLineWidth(0.75);
  doc.line(plotX, y0 + plotH, toX(max), y0 + plotH);

  drawBar(pdf, 'U.S. average', estimate.averageRisk, COLOR.averageBar, y0 + 24.56, plotX, toX, x0);
  drawBar(pdf, RISK_COPY[estimate.kind].label, estimate.risk, COLOR.riskBar, y0 + 52.52, plotX, toX, x0);

  pdf.y = top + boxH;
}

function drawBar(
  pdf: PdfWriter,
  label: string,
  value: number,
  color: string,
  top: number,
  plotX: number,
  toX: (percent: number) => number,
  x0: number,
) {
  const doc = pdf.doc;
  const h = 17.39;
  const end = toX(value);
  const w = end - plotX;

  doc.setFillColor(color);
  if (w > 0) {
    // Square left edge on the axis, rounded right end
    const r = Math.min(h / 2, w);
    doc.roundedRect(plotX, top, w, h, r, r, 'F');
    doc.rect(plotX, top, Math.min(r, w), h, 'F');
  }

  // Value sits just past the bar end so it stays readable on small bars;
  // the white backing keeps grid lines from running through it
  const valueText = `${formatPercent(value, true)}%`;
  doc.setFont('helvetica', 'bold');
  const valueWidth = pdf.measure(valueText, 12);
  doc.setFillColor('#FFFFFF');
  doc.rect(end + 2, top + 2, valueWidth + 4, h - 4, 'F');
  pdf.text(valueText, end + 4, top + h / 2, { size: 12, bold: true, baseline: 'middle' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  const lines: string[] = doc.splitTextToSize(label, 70);
  const lineH = 11;
  const startY = top + h / 2 - (lines.length * lineH) / 2 + lineH / 2;
  lines.forEach((line, i) => pdf.text(line, x0 + 68, startY + i * lineH, { size: 10, bold: true, align: 'right', baseline: 'middle' }));
}

// Header/Data Export - Figma node 61:3948
function drawHeader(doc: jsPDF, logo: string, options: BcraPdfOptions) {
  doc.setFillColor(COLOR.headerBg);
  doc.rect(0, 0, PAGE_W, 51, 'F');
  doc.setFillColor(COLOR.divider);
  doc.rect(41, 50, 530, 2, 'F');

  doc.setFillColor(COLOR.logoBg);
  doc.rect(0, 0, 51, 51, 'F');
  doc.addImage(logo, 'PNG', 10.2, 10.08, 30.91, 30.91);

  doc.setTextColor(COLOR.black);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('IVY BREAST CANCER RISK ASSESSMENT REPORT', 70, 25.5, { baseline: 'middle', charSpace: 0.65 });

  doc.setFontSize(8);
  doc.text(options.userName, PAGE_W - 16, 20.5, { align: 'right', baseline: 'middle' });
  doc.setFont('helvetica', 'normal');
  doc.text(formatLongDate(options.generatedAt), PAGE_W - 16, 30.5, { align: 'right', baseline: 'middle' });
}

// Footer/Data Export - Figma node 61:3990
function drawFooters(doc: jsPDF) {
  const total = doc.getNumberOfPages();
  for (let page = 1; page <= total; page++) {
    doc.setPage(page);
    doc.setFillColor(COLOR.headerBg);
    doc.rect(0, 754, PAGE_W, 38, 'F');
    doc.setTextColor(COLOR.footerText);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('www.ivyhealth.ai', 16, 773, { baseline: 'middle' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(`${page}/${total}`, PAGE_W - 16, 773, { align: 'right', baseline: 'middle' });
  }
}

/** Keeps a vertical cursor and lays out wrapped, mixed-weight text inside the content column. */
class PdfWriter {
  y = CONTENT_TOP;

  constructor(readonly doc: jsPDF, private onNewPage: () => void) {}

  newPage() {
    this.doc.addPage('letter');
    this.onNewPage();
    this.y = CONTENT_TOP;
  }

  ensure(height: number) {
    if (this.y + height > CONTENT_BOTTOM) {
      this.newPage();
    }
  }

  gap(height: number) {
    this.y += height;
  }

  heading(text: string, gapAfter = GAP) {
    this.paragraph([{ text, bold: true }]);
    this.gap(gapAfter);
  }

  paragraph(runs: Run[], opts: { size?: number; line?: number; align?: 'left' | 'center'; x?: number; width?: number } = {}) {
    const size = opts.size ?? TEXT;
    const line = opts.line ?? LINE;
    const x = opts.x ?? CONTENT_X;
    const width = opts.width ?? CONTENT_W;
    for (const words of this.wrap(runs, width, size)) {
      this.ensure(line);
      this.drawLine(words, x, width, size, line, opts.align ?? 'left');
      this.y += line;
    }
  }

  /** Tinted rounded box around a paragraph. */
  box(runs: Run[], opts: { padX: number; padY: number; align?: 'left' | 'center' }) {
    const width = CONTENT_W - opts.padX * 2;
    const lines = this.wrap(runs, width, TEXT);
    const height = lines.length * LINE + opts.padY * 2;
    this.ensure(height);
    this.doc.setFillColor(COLOR.tint);
    this.doc.roundedRect(CONTENT_X, this.y, CONTENT_W, height, 10, 10, 'F');
    this.y += opts.padY;
    for (const words of lines) {
      this.drawLine(words, CONTENT_X + opts.padX, width, TEXT, LINE, opts.align ?? 'left');
      this.y += LINE;
    }
    this.y += opts.padY;
  }

  /** Fully rounded tag sized to its text. */
  pill(text: string) {
    const height = 28;
    this.ensure(height);
    this.doc.setFont('helvetica', 'bold');
    const width = this.measure(text, TEXT) + 20;
    this.doc.setFillColor(COLOR.tint);
    this.doc.roundedRect(CONTENT_X, this.y, width, height, height / 2, height / 2, 'F');
    this.text(text, CONTENT_X + 10, this.y + height / 2, { size: TEXT, bold: true, baseline: 'middle' });
    this.y += height;
  }

  text(text: string, x: number, y: number, opts: { size: number; bold?: boolean; align?: 'left' | 'center' | 'right'; baseline?: 'top' | 'middle' }) {
    this.doc.setTextColor(COLOR.black);
    this.doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
    this.doc.setFontSize(opts.size);
    this.doc.text(text, x, y, { align: opts.align ?? 'left', baseline: opts.baseline ?? 'top' });
  }

  private drawLine(words: Word[], x: number, width: number, size: number, line: number, align: 'left' | 'center') {
    const lineWidth = words.reduce((sum, w) => sum + w.width, 0);
    let cursor = align === 'center' ? x + (width - lineWidth) / 2 : x;
    for (const word of words) {
      this.text(word.text, cursor, this.y + line / 2, { size, bold: word.bold, baseline: 'middle' });
      cursor += word.width;
    }
  }

  /**
   * Width as drawn. getTextWidth applies kerning, but standard-font text is rendered without it,
   * so words placed one by one would overlap the following space (e.g. after "Your").
   */
  measure(text: string, size: number): number {
    return this.doc.getStringUnitWidth(text, { kerning: {} } as any) * size / this.doc.internal.scaleFactor;
  }

  private wrap(runs: Run[], width: number, size: number): Word[][] {
    this.doc.setFontSize(size);
    const lines: Word[][] = [[]];
    let lineWidth = 0;
    for (const run of runs) {
      const bold = !!run.bold;
      this.doc.setFont('helvetica', bold ? 'bold' : 'normal');
      for (const token of run.text.split(/(\s+)/).filter(Boolean)) {
        const isSpace = /^\s+$/.test(token);
        const tokenWidth = this.measure(isSpace ? ' ' : token, size);
        let current = lines[lines.length - 1];
        if (!isSpace && lineWidth + tokenWidth > width && current.length) {
          // Drop the trailing space before breaking
          if (/^\s+$/.test(current[current.length - 1].text)) {
            lineWidth -= current.pop()!.width;
          }
          lines.push([]);
          current = lines[lines.length - 1];
          lineWidth = 0;
        }
        if (isSpace && !current.length) continue;
        current.push({ text: isSpace ? ' ' : token, bold, width: tokenWidth });
        lineWidth += tokenWidth;
      }
    }
    return lines.filter((line) => line.length);
  }
}

/** jsPDF can't draw SVG, so the logo is drawn to a canvas at a higher scale and embedded as PNG. */
async function rasterizeSvg(url: string, size: number, scale: number): Promise<string> {
  const svg = await (await fetch(url)).text();
  const image = new Image();
  image.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  await image.decode();
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size * scale;
  canvas.getContext('2d')!.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}

function formatPercent(value: number, fixed = false): string {
  return fixed ? value.toFixed(1) : String(Math.round(value * 10) / 10);
}

/** The PDF sets chart captions in title case, like the page's `capitalize`. */
function toTitleCase(text: string): string {
  return text.replace(/(^|\s)(\S)/g, (_, space, letter) => space + letter.toUpperCase());
}

function formatDayMonthYear(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
